import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { HttpClientWithInFlightCache } from '../../global/httpClient';
import { ArmyBuilderConfig } from '../../global/config';
import { FeatureFlagService } from '../../global/feature-flag/feature-flag.service';
import { FeatureFlag } from '../../global/feature-flag/models';

const ALL_ROLES = ['app_admin', 'app_editor', 'app_user', 'app_user_beta', 'app_free_trial', 'app_subscriber', 'app_subscriber_annual'];

@Component({
    standalone: true,
    selector: 'abs-feature-flag-admin',
    imports: [CommonModule, IonicModule, FormsModule],
    template: `
        <ion-header>
            <ion-toolbar>
                <ion-title>Feature Flags</ion-title>
            </ion-toolbar>
        </ion-header>
        <ion-content class="ion-padding">
            <p>
                Manage feature flags. Each flag has a label used in code (via route guards or the
                <code>*featureFlag</code> directive) and a list of roles that can access the feature. If no roles are specified, the feature
                is visible to everyone (when enabled).
            </p>

            <ion-card class="new-flag">
                <ion-card-header>
                    <ion-card-title>Add Feature Flag</ion-card-title>
                </ion-card-header>
                <ion-card-content>
                    <ion-item>
                        <ion-input
                            label="Feature Label"
                            labelPlacement="stacked"
                            placeholder="e.g. events"
                            [(ngModel)]="newFeature"
                        ></ion-input>
                    </ion-item>
                    <div class="role-list">
                        @for (role of allRolesForNew(); track role) {
                            <ion-chip [color]="newRoles().includes(role) ? 'success' : 'medium'" (click)="toggleNewRole(role)">
                                <ion-label>{{ role }}</ion-label>
                            </ion-chip>
                        }
                    </div>
                    <ion-item>
                        <ion-input
                            label="Custom role"
                            labelPlacement="stacked"
                            placeholder="e.g. k47_image_editor"
                            [(ngModel)]="newCustomRole"
                            (keyup.enter)="addCustomRoleToNew()"
                        ></ion-input>
                        <ion-button slot="end" fill="outline" size="small" [disabled]="!newCustomRole.trim()" (click)="addCustomRoleToNew()"
                            >Add role</ion-button
                        >
                    </ion-item>
                    <ion-button [disabled]="!newFeature" (click)="addFlag()">Add</ion-button>
                </ion-card-content>
            </ion-card>

            <ion-list>
                @for (flag of featureFlagService.flags(); track flag.feature) {
                    <ion-card>
                        <ion-card-header>
                            <ion-card-title class="flag-header">
                                <span>{{ flag.feature }}</span>
                                <div class="flag-actions">
                                    <ion-toggle [checked]="flag.enabled" (ionChange)="toggleEnabled(flag, $event)"></ion-toggle>
                                    <ion-button fill="clear" color="danger" size="small" (click)="deleteFlag(flag)">
                                        <ion-icon name="trash-outline" slot="icon-only"></ion-icon>
                                    </ion-button>
                                </div>
                            </ion-card-title>
                        </ion-card-header>
                        <ion-card-content>
                            <div class="role-list">
                                @for (role of allRolesForFlag(flag); track role) {
                                    <ion-chip
                                        [color]="flag.requiredRoles.includes(role) ? 'success' : 'medium'"
                                        (click)="toggleFlagRole(flag, role)"
                                    >
                                        <ion-label>{{ role }}</ion-label>
                                    </ion-chip>
                                }
                            </div>
                            <ion-item>
                                <ion-input
                                    label="Custom role"
                                    labelPlacement="stacked"
                                    placeholder="e.g. k47_image_editor"
                                    [ngModel]="customRoleInputs[flag.feature] || ''"
                                    (ngModelChange)="customRoleInputs[flag.feature] = $event"
                                    (keyup.enter)="addCustomRoleToFlag(flag, customRoleInputs[flag.feature])"
                                ></ion-input>
                                <ion-button
                                    slot="end"
                                    fill="outline"
                                    size="small"
                                    [disabled]="!customRoleInputs[flag.feature]?.trim()"
                                    (click)="addCustomRoleToFlag(flag, customRoleInputs[flag.feature])"
                                    >Add role</ion-button
                                >
                            </ion-item>
                            @if (flag.requiredRoles.length === 0) {
                                <p class="note">No roles specified — visible to all users when enabled.</p>
                            }
                        </ion-card-content>
                    </ion-card>
                }
            </ion-list>

            @if (featureFlagService.flags().length === 0) {
                <p>No feature flags configured yet.</p>
            }
        </ion-content>
    `,
    styles: [
        `
            .flag-header {
                display: flex;
                align-items: center;
                justify-content: space-between;
            }
            .flag-actions {
                display: flex;
                align-items: center;
                gap: 8px;
            }
            .role-list {
                display: flex;
                flex-wrap: wrap;
                gap: 4px;
                margin: 8px 0;
            }
            .note {
                font-size: 13px;
                color: var(--ion-color-medium);
                margin: 4px 0 0;
            }
            code {
                background: var(--ion-color-light);
                padding: 1px 4px;
                border-radius: 3px;
                font-size: 13px;
            }
        `
    ]
})
export class FeatureFlagAdminPage {
    private http = inject(HttpClientWithInFlightCache);
    private config = inject(ArmyBuilderConfig);
    featureFlagService = inject(FeatureFlagService);

    allRoles = ALL_ROLES;
    newFeature = '';
    newRoles = signal<string[]>([]);
    newCustomRole = '';
    /** Per-flag text input state for adding custom roles. */
    customRoleInputs: Record<string, string> = {};

    private get baseUrl() {
        return `${this.config.apiBaseUrl}/feature-flags`;
    }

    /** All roles for the new-flag form: the fixed list plus any custom roles already added. */
    allRolesForNew() {
        return [...new Set([...ALL_ROLES, ...this.newRoles()])];
    }

    /** All roles for an existing flag: the fixed list plus any custom roles already on the flag. */
    allRolesForFlag(flag: FeatureFlag): string[] {
        return [...new Set([...ALL_ROLES, ...flag.requiredRoles])];
    }

    addCustomRoleToNew() {
        const role = this.newCustomRole.trim();
        if (!role) return;
        if (!this.newRoles().includes(role)) {
            this.newRoles.set([...this.newRoles(), role]);
        }
        this.newCustomRole = '';
    }

    addCustomRoleToFlag(flag: FeatureFlag, role: string) {
        if (!role?.trim()) return;
        const trimmed = role.trim();
        if (!flag.requiredRoles.includes(trimmed)) {
            const roles = [...flag.requiredRoles, trimmed];
            this.http.put(`${this.baseUrl}/${flag.feature}`, { requiredRoles: roles }).subscribe(() => this.featureFlagService.loadFlags());
        }
        this.customRoleInputs[flag.feature] = '';
    }

    toggleNewRole(role: string) {
        const current = this.newRoles();
        if (current.includes(role)) {
            this.newRoles.set(current.filter((r) => r !== role));
        } else {
            this.newRoles.set([...current, role]);
        }
    }

    addFlag() {
        if (!this.newFeature.trim()) return;
        this.http
            .put(`${this.baseUrl}/${this.newFeature.trim()}`, {
                requiredRoles: this.newRoles(),
                enabled: true
            })
            .subscribe(() => {
                this.newFeature = '';
                this.newRoles.set([]);
                this.newCustomRole = '';
                this.featureFlagService.loadFlags();
            });
    }

    toggleEnabled(flag: FeatureFlag, event: any) {
        this.http
            .put(`${this.baseUrl}/${flag.feature}`, { enabled: event.detail.checked })
            .subscribe(() => this.featureFlagService.loadFlags());
    }

    toggleFlagRole(flag: FeatureFlag, role: string) {
        const roles = flag.requiredRoles.includes(role) ? flag.requiredRoles.filter((r) => r !== role) : [...flag.requiredRoles, role];
        this.http.put(`${this.baseUrl}/${flag.feature}`, { requiredRoles: roles }).subscribe(() => this.featureFlagService.loadFlags());
    }

    deleteFlag(flag: FeatureFlag) {
        this.http.delete(`${this.baseUrl}/${flag.feature}`).subscribe(() => this.featureFlagService.loadFlags());
    }
}
